
import { defineComponent, reactive, onBeforeMount, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import FtlOrderState from '@/modules/FTLModule/components/FtlOrderState/index.vue'
import FtlOrderHeader from '@/modules/FTLModule/components/FtlOrderHeader/index.vue'
import FtlOrderTabs from '@/modules/FTLModule/components/FtlOrderTabs/index.vue'

import FtlOrderGeneral from '@/modules/FTLModule/components/FtlOrderGeneral/index.vue'
import FtlOrderCargo from '@/modules/FTLModule/components/FtlOrderCargo/index.vue'
import FtlOrderPayment from '@/modules/FTLModule/components/FtlOrderPayment/index.vue'
import FtlOrderSenderAndStops from '@/modules/FTLModule/components/FtlOrderSenderAndStops/index.vue'
import FtlOrderVehicle from '@/modules/FTLModule/components/FtlOrderVehicle/index.vue'
import FtlOrderFiles from '@/modules/FTLModule/components/FtlOrderFiles/index.vue'
import FtlOrderStatuses from '@/modules/FTLModule/components/FtlOrderStatuses/index.vue'
import EnterCarrierDataIntracity from '@/modules/FTLBidsModule/components/EnterCarrierDataIntracity/index.vue'
import EnterDeliveredDataIntracity from '@/modules/OrdersHistoryModule/components/EnterDeliveredDataIntracity/index.vue'

import FtlOnTheWay from '@/modules/OrdersHistoryModule/components/FtlOnTheWay/index.vue'
import EnterCompletedDataIntracity from '@/modules/OrdersHistoryModule/components/EnterCompletedDataIntracity/index.vue'
import UploadFtlOrderScan from '@/modules/OrdersHistoryModule/components/UploadFtlOrderScan/index.vue'
import UploadFtlStockScan from '@/modules/OrdersHistoryModule/components/UploadFtlStockScan/index.vue'

// types
import { FullFTLOrderT, StockDeliveryNoteT } from '@/core/types/orders.types'

// constants
import { FtlFileNames } from '@/core/constants/columns/FTL.columns'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { ftl_bids } from '@/core/realisations/ftl_bids.realisation'
import { FTL } from '@/core/realisations/FTL.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'
import { CompleteIntracityPayload } from '@/core/types/additional_services.types'

// validation
import {
    EmitCarrierDataIntracityParams,
    EnterCarrierDataIntracityParams,
} from '@/core/types/ftl_bids.types'

export default defineComponent({
    components: {
        FtlOrderHeader,
        FtlOrderState,
        FtlOrderTabs,
        FtlOrderGeneral,
        FtlOrderPayment,
        FtlOrderSenderAndStops,
        FtlOrderVehicle,
        FtlOrderFiles,
        FtlOrderStatuses,
        FtlOrderCargo,
        //
        FtlOnTheWay,
        EnterCompletedDataIntracity,
        UploadFtlOrderScan,
        UploadFtlStockScan,
        EnterCarrierDataIntracity,
        EnterDeliveredDataIntracity,
    },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const notifications = useNotifications()
        const { getFormData } = useFormData()

        const order_id = Number(route.params.order_id)

        const state = reactive({
            is_loading: true,
            is_failed: false,
            ftlOrder: null as FullFTLOrderT | null,
            currentTab: 'general',
            is_delay_loading: false,
            //
            is_status_loading: false,
            //
            is_on_the_way_loading: false,
            is_on_the_way: false,
            //
            is_transcript_printing: false,
            is_route_printing: false,
            is_resending_to_one_c: false,
            is_invoices_printing: false,
            //
            is_scan_upload: false,
            //
            is_already_have_one_c: false,
            //
            is_printing_stock_invoice: false,
            is_carrier_loading: false,
            is_carrier: false,
            is_delivered_intracity: false,
            is_delivered_intracity_loading: false,
            is_complete_intracity: false,
            is_complete_intracity_loading: false,
        })

        const upload_ftl_stock_scan = reactive({
            is_opened: false,
            ftl_stock_id: null as number | null,
            ftl_stock_directionName: null as string | null,
            ftl_stock_number: null as string | null,
        })

        const uploadStockScanHandler = (stockDeliveryNote: StockDeliveryNoteT) => {
            upload_ftl_stock_scan.ftl_stock_id = stockDeliveryNote.id
            upload_ftl_stock_scan.ftl_stock_directionName = stockDeliveryNote.directionName
            upload_ftl_stock_scan.ftl_stock_number = stockDeliveryNote.number
            upload_ftl_stock_scan.is_opened = true
        }

        const uploadStockScanCancel = () => {
            upload_ftl_stock_scan.is_opened = false
            upload_ftl_stock_scan.ftl_stock_id = null
            upload_ftl_stock_scan.ftl_stock_directionName = null
            upload_ftl_stock_scan.ftl_stock_number = null
        }

        const uploadStockScanSubmit = () => {
            uploadStockScanCancel()
            loadFtlOrder()
        }

        const loadFtlOrder = async () => {
            try {
                state.is_loading = true

                const response = await orders.getFTLOrder(order_id)

                if (response.data.ftl_type !== 2) {
                    router.replace(`/ftl-orders/${order_id}`)
                }

                state.ftlOrder = response.data

                state.is_failed = false
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При получении FTL заказа произошла ошибка',
                })

                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const checkAvr = async () => {
            try {
                state.is_loading = true
                const response = await ftl_bids.checkAvr(order_id)
                state.is_already_have_one_c = response.data.isPosted
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadFtlOrder()
            checkAvr()
        })

        const assignStatus = async (status_code: string) => {
            if (status_code === 'canceled') {
                const answer = confirm('Вы действительно хотите отменить FTL заказ?')
                if (!answer) return
            }

            try {
                state.is_status_loading = true

                const params = {
                    id: order_id,
                    status_code,
                }

                await ftl_bids.changeStatusFTLOrder(order_id, getFormData(params))

                notifications.notify({
                    title: 'Статус изменен',
                    type: 'success',
                })

                await loadFtlOrder()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При изменении статуса произошла ошибка!',
                })
            } finally {
                state.is_status_loading = false
            }
        }

        const assignStatusOnTheWayHandler = () => {
            if (state.ftlOrder) {
                state.is_on_the_way = true
            }
        }

        const onTheWaySubmit = async (payload: FormData) => {
            try {
                state.is_on_the_way_loading = true

                await ftl_bids.changeStatusFTLOrder(order_id, payload)

                notifications.notify({
                    title: 'Статус изменен',
                    type: 'success',
                })

                state.is_on_the_way = false

                await loadFtlOrder()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При изменении статуса произошла ошибка!',
                })
            } finally {
                state.is_on_the_way_loading = false
            }
        }

        const onTheWayCancel = () => {
            if (state.is_on_the_way_loading) return
            state.is_on_the_way = false
        }

        const toggleDelivered = () => {
            state.is_delivered_intracity = !state.is_delivered_intracity
        }

        const toggleCompleteIntracity = () =>
            (state.is_complete_intracity = !state.is_complete_intracity)

        const onDeliveredIntracitySubmit = async (delivery_data: any) => {
            try {
                state.is_delivered_intracity_loading = true

                const params: any = {
                    delivery_date: delivery_data.delivery_date,
                    delivery_time: delivery_data.delivery_time,
                    received_by: delivery_data.received_by,
                    auto_count: delivery_data.auto_count,
                    auto_duration: delivery_data.auto_duration,
                    additional_services: delivery_data.additional_services,
                }

                if (delivery_data.delayCommentId) {
                    params.delayCommentId = delivery_data.delayCommentId
                }

                if (delivery_data.has_downtime) {
                    params.has_downtime = delivery_data.has_downtime
                    params.downtime = delivery_data.downtime
                }

                await ftl_bids.enterDeliveryDataIntracity(order_id, params)

                notifications.notify({
                    title: 'Данные о доставке успешно внесены',
                    type: 'success',
                })

                toggleDelivered()
                loadFtlOrder()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При внесении данных о доставке произошла ошбика',
                })
            } finally {
                state.is_delivered_intracity_loading = false
            }
        }

        const onDelieveredIntracityCancel = () => {
            if (state.is_delivered_intracity_loading) return
            state.is_delivered_intracity = false
        }

        const onCompleteIntracitySubmit = async (payload?: CompleteIntracityPayload) => {
            try {
                state.is_complete_intracity_loading = true

                await ftl_bids.completeIntracity(order_id, payload)

                notifications.notify({
                    title: 'Заказ завершен',
                    type: 'success',
                })

                toggleCompleteIntracity()
                loadFtlOrder()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При завершении заказа произошла ошбика',
                })
            } finally {
                state.is_complete_intracity_loading = false
            }
        }

        const onCompleteIntracityCancel = () => {
            if (state.is_complete_intracity_loading) return
            state.is_complete_intracity = false
        }

        const onPrintTranscript = async () => {
            try {
                state.is_transcript_printing = true

                const response = await ftl_bids.exportExcel(order_id)

                const download_url = window.URL.createObjectURL(response)

                const link = document.createElement('a')
                link.href = download_url
                link.target = '_blank'
                link.setAttribute('download', 'Транскрипт')
                document.body.appendChild(link)
                link.click()
                link.remove()
            } catch (e) {
                console.log(e)
            } finally {
                state.is_transcript_printing = false
            }
        }

        const saveFile = (content: Blob, file_name: string) => {
            const download_url = window.URL.createObjectURL(content)

            const link = document.createElement('a')
            link.href = download_url
            link.setAttribute('download', file_name)
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const onPrintRoute = async () => {
            try {
                state.is_route_printing = true

                const response = await FTL.printRoute(order_id)
                saveFile(response, `FTL рейс №${order_id}.pdf`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При распечатке рейса произошла ошибка' })
            } finally {
                state.is_route_printing = false
            }
        }

        const assignStatusCanceled = () => {
            assignStatus('canceled')
        }

        const onPrintInvoices = async () => {
            if (state.is_invoices_printing) return

            try {
                state.is_invoices_printing = true

                const response = await FTL.printFtlOrderInvoices(order_id)
                saveFile(response, `Накладные FTL заказа ${order_id}`)
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При распечатке накладных произошла ошибка',
                })
            } finally {
                state.is_invoices_printing = false
            }
        }

        const onDownloadScan = () => {
            const link = document.createElement('a')
            link.href = (state.ftlOrder as FullFTLOrderT).scan as string
            link.target = '_blank'
            link.setAttribute('download', `Скан FTL заказ №${order_id}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const onScanUploadHandler = () => {
            state.is_scan_upload = true
        }

        const onScanUploadSubmit = () => {
            state.is_scan_upload = false
            loadFtlOrder()
        }

        const onScanUploadCancel = () => {
            state.is_scan_upload = false
        }

        const printStockInvoiceHandler = async (index: number) => {
            if (state.is_printing_stock_invoice) return

            try {
                state.is_printing_stock_invoice = true

                const response = await FTL.printFtlOrderStockDeliveryNoteInvoice(order_id, index)
                saveFile(response, `Накладная FTL заказа ${order_id} пункта ${index + 1}`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При распечатке накладной произошла ошибка',
                })
            } finally {
                state.is_printing_stock_invoice = false
            }
        }

        const toggleCarrier = () => (state.is_carrier = !state.is_carrier)

        const getCarrierParams = (
            ftl_type: number,
            shipment_type: number,
            carrier: EmitCarrierDataIntracityParams
        ): EnterCarrierDataIntracityParams | null => {
            if (!state.ftlOrder) return null

            if (ftl_type === 1 && shipment_type === 2) {
                return {
                    id: state.ftlOrder.id,
                    carrier_id: carrier.carrier_id,
                }
            } else {
                const params: EnterCarrierDataIntracityParams = {
                    id: state.ftlOrder.id,
                    ...carrier,
                }

                if (carrier.payment_rate_type_id === 1) {
                    params.cost_price_per_hour = carrier.cost_price
                    delete params.cost_price
                }

                return params
            }
        }

        const enterCarrierDataSubmit = async (carrier: EmitCarrierDataIntracityParams) => {
            if (!state.ftlOrder) return
            if (state.is_carrier_loading) return

            try {
                state.is_carrier_loading = true

                const params = getCarrierParams(
                    state.ftlOrder.ftl_type,
                    state.ftlOrder.shipment_type_id,
                    carrier
                )

                if (!params) return

                await ftl_bids.enterCarrierDataIntracity(params as EnterCarrierDataIntracityParams)

                notifications.notify({
                    title: 'Данные о перевозчике заполнены',
                    type: 'success',
                })

                toggleCarrier()
                loadFtlOrder()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При внесении данных о перевозчике произошла ошибка!',
                })
            } finally {
                state.is_carrier_loading = false
            }
        }

        const ftlOrderFilesArray = computed(() => {
            if (!state.ftlOrder) return []

            return Object.entries(state.ftlOrder.files).map(([key, file]) => {
                return {
                    id: `file-${key}`,
                    path: file.url,
                    original_name: file.fileName,
                    title: FtlFileNames[key as keyof typeof FtlFileNames] || 'Файл без типа',
                }
            })
        })

        const senderCity = computed(() => {
            if (state.ftlOrder) {
                if (state.ftlOrder.sender) {
                    return state.ftlOrder.sender.city
                }
            }

            return null
        })

        const onResendToOneC = async () => {
            try {
                state.is_resending_to_one_c = true

                await ftl_bids.resendOneC(order_id)

                loadFtlOrder()
                notifications.notify({
                    title: 'Заказ успешно отправлен в 1С!',
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При отправке заказа произошла ошибка',
                })
            } finally {
                state.is_resending_to_one_c = false
            }
        }

        return {
            state,
            upload_ftl_stock_scan,
            loadFtlOrder,
            assignStatusOnTheWayHandler,
            onTheWaySubmit,
            onTheWayCancel,
            onDeliveredIntracitySubmit,
            onDelieveredIntracityCancel,
            onCompleteIntracitySubmit,
            onCompleteIntracityCancel,
            onPrintTranscript,
            onPrintRoute,
            assignStatusCanceled,
            onPrintInvoices,
            onDownloadScan,
            onScanUploadHandler,
            onScanUploadSubmit,
            onScanUploadCancel,
            uploadStockScanHandler,
            uploadStockScanCancel,
            uploadStockScanSubmit,
            printStockInvoiceHandler,
            ftlOrderFilesArray,
            senderCity,
            toggleCarrier,
            toggleDelivered,
            enterCarrierDataSubmit,
            toggleCompleteIntracity,
            onResendToOneC,
        }
    },
})
